import React from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Button,
  Tooltip,
} from "@mui/material";
import snowman from "../images/snowman.png";
import snowflake from "../images_new/snowflake.svg";
import Colors from "../components/Colors";
import GiftIcon from "@mui/icons-material/CardGiftcard"; // Import a gift icon

const LEVELS = [
  { points: 100, color: Colors.yellowflake },
  { points: 300, color: Colors.orengeflake },
  { points: 600, color: Colors.redflake },
  { points: 1000, color: Colors.greenflake },
];

const GameLevelProgress = ({ points }) => {
  const getProgressPercentage = (currentPoints, fromPoints, toPoints) => {
    if (currentPoints < fromPoints) return 0;
    if (currentPoints >= fromPoints && currentPoints < toPoints) {
      return ((currentPoints - fromPoints) / (toPoints - fromPoints)) * 100;
    }
    return 100;
  };

  const handleRedeemGift = () => {
    // Redirect to the gift shopping page
    window.location.href = "/gift"; // Update the URL as needed
  };

  // Check if the user has completed any level
  const completedLevel = LEVELS.find((level) => points >= level.points);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={2}
      marginTop={{ md: "-16px", xs: "8px" }}
    >
      {/* Progress Bar Section */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="row"
        gap={"2px"}
      >
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              "& img": {
                width: { xs: "26px", md: "60px" },
                height: { xs: "26px", md: "60px" },
              },
            }}
          >
            <img src={snowman} alt="snowman" />
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center">
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage(points, 0, LEVELS[0].points)}
            sx={{
              height: 7,
              borderRadius: 4,
              border: "1px solid",
              borderColor: "#D3D3D3",
              background: "white",
              width: { md: "150px", xs: "46px" },
              "& .MuiLinearProgress-bar": {
                borderRadius: 5,
                backgroundColor: LEVELS[0].color,
              },
            }}
          />
        </Box>
        {LEVELS.map((level, index) => (
          <React.Fragment key={index}>
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={{ md: 36, xs: 16 }}
                height={{ md: 36, xs: 16 }}
                borderRadius="50%"
                bgcolor={level.color}
                marginTop={"20px"}
              >
                <img
                  src={snowflake}
                  alt="Snowflake"
                  style={{
                    width: "70%",
                    height: "70%",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: { md: "14px", xs: "10px" },
                  mt: 0.5,
                  fontWeight: 500,
                  color: points >= level.points ? level.color : "gray",
                }}
              >
                {level.points}
              </Typography>
            </Box>
            {index < LEVELS.length - 1 && (
              <Box display="flex" justifyContent="center" alignItems="center">
                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage(
                    points,
                    LEVELS[index].points,
                    LEVELS[index + 1].points,
                  )}
                  sx={{
                    height: 7,
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: "#D3D3D3",
                    background: "white",
                    width: { md: "150px", xs: "46px" },
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      backgroundColor: LEVELS[index + 1].color,
                    },
                  }}
                />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>

      {/* Redeem Gift Button */}
      {/* <Tooltip title={completedLevel ? "Redeem your gift!" : "Complete a level to redeem a gift"} arrow> */}
      <Box>
        <Button
          variant="contained"
          onClick={handleRedeemGift}
          disabled={!completedLevel}
          sx={{
            background: completedLevel
              ? "linear-gradient(45deg, #FF416C 0%, #FF4B2B 100%)" // Gradient for enabled state
              : "#D3D3D3", // Gray for disabled state
            color: "white",
            borderRadius: "20px",
            padding: { xs: "4px 8px", md: "8px 16px" }, // Responsive padding
            fontSize: { xs: "12px", sm: "13px", md: "14px" }, // Responsive font size
            fontWeight: "bold",
            textTransform: "none",
            boxShadow: completedLevel
              ? "0px 4px 6px rgba(255, 111, 97, 0.3)" // Shadow for enabled state
              : "none", // No shadow for disabled state
            transition: "all 0.3s ease",
            position: "relative",
            overflow: "hidden",
            "&:hover": {
              background: completedLevel
                ? "linear-gradient(45deg, #FF4A3D 0%, #FF6F61 100%)" // Reverse gradient on hover
                : "#D3D3D3",
              boxShadow: completedLevel
                ? "0px 6px 12px rgba(255, 111, 97, 0.5)" // Larger shadow on hover
                : "none",
              transform: completedLevel ? "scale(1.05)" : "none", // Slight scale on hover
            },
            "&:disabled": {
              background: "#D3D3D3",
              color: "#A9A9A9",
              boxShadow: "none",
            },
            "& .MuiButton-startIcon": {
              // Responsive icon size
              "& svg": {
                fontSize: { xs: "18px", md: "24px" }, // Smaller icon on mobile, larger on desktop
              },
            },
            "&::before": {
              // Ripple effect background
              content: '""',
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "300%",
              height: "300%",
              background: "rgba(255, 255, 255, 0.1)",
              borderRadius: "50%",
              transform: "translate(-50%, -50%) scale(0)",
              transition: "transform 0.5s ease",
            },
            "&:active::before": {
              // Ripple effect on click
              transform: "translate(-50%, -50%) scale(1)",
              opacity: 0,
            },
          }}
          startIcon={<GiftIcon />} // Add a gift icon
        >
          Redeem Your Gift
        </Button>
      </Box>
      {/* </Tooltip> */}
    </Box>
  );
};

export default GameLevelProgress;
